import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {EventHandler} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChannelErrorModel} from '../../../../libs/shared/core/src/models/api/channelErrorModel';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {ColorDot} from '../../../../libs/shared/ui/src/components/utility/colorDot';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {CellsConversationsDragItem} from '../../../helpers/conversationCellHelpers';
import {inboxHasColor} from '../../../helpers/modelHelpers';
import {isActionableError} from '../../../helpers/models/channelHelpers';
import {NavigationLineErrorIcon} from '../../common/navigation/navigationLineErrorIcon';
import {NavigationLineIcon} from '../../common/navigation/navigationLineIcon';
import {NavigationLineLabel, NavigationLineLabelProps} from '../../common/navigation/navigationLineLabel';
import {SafetyNetActionsEnum} from '../viewer/conversation/safetyNets/safetyNetsContext';
import {useConversationIdsHandlerWithSafetyNets} from '../viewer/conversation/safetyNets/safetyNetsCoordinator';
import {MoveAccessDialog} from '../viewer/conversation/topbar/move/moveAccessDialogContainer';
import {WorkspacesEditorRarelyViewedBadge} from './editor/workspacesEditorRarelyViewedBadge';
import {WorkspaceLineDropTarget, WorkspaceLineDropTargetProps} from './line/workspaceLineDropTarget';
import {WorkspaceLineMore, WorkspaceLineMoreProps} from './line/workspaceLineMore';
import {StyledLabelDiv, StyledTextDiv} from './workspaceCustomViewItem';
import {WorkspaceItemRefinedResultsIndicator} from './workspaceItemRefinedResultsIndicator';

/*
 * Props.
 */

export interface WorkspaceInboxItemProps
  extends WorkspaceLineDropTargetProps,
    NavigationLineLabelProps,
    Omit<WorkspaceLineMoreProps, 'isGuidedDemoActive'> {
  /** The inbox to display. */
  inbox: InboxModel;
  /** Should the inbox name be bold. */
  isUnread?: boolean;
  /** Channel error to display, if this inbox has channels in an error state. */
  channelError?: ChannelErrorModel;
  /** If a channel has an error, whether the error is user-dismissable. */
  errorIsActionable?: boolean;
  /** Display banner with current inbox errors. */
  onShowInboxErrorsClick: () => void;
  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  /** Should the checkbox be selected if we're in editing mode. */
  isChecked?: boolean;
  isRecentlyViewed?: boolean;
  findInboxUnreachableState?: (
    conversationIds: ReadonlyArray<number>,
    inbox: InboxModel,
  ) => InboxUnreachableState;
  hasNewMoveToUnreachable?: boolean;
  shouldShowRefinedResultsIndicator: boolean;
}

export interface InboxUnreachableState {
  isInboxUnreachable: boolean;
  isUserOnlyFollower: boolean;
}

const StyledWorkspaceItemRefinedResultsIndicator = styled(WorkspaceItemRefinedResultsIndicator)`
  margin-left: 4px;
`;

/*
 * Component.
 */

/** Display an inbox as a P1 workspace line. */
export const WorkspaceInboxItem: FC<WorkspaceInboxItemProps> = (props) => {
  const {inbox, isUnread, isSelected, isEditingWorkspace} = props;

  const theme = useUserTheme();
  const defaultColor = isSelected ? theme.itemDotHover : theme.itemDot;
  const inboxColor = (inboxHasColor(inbox) && inbox.color) || undefined;
  const [inboxUnreachableState, setInboxUnreachableState] = useState<
    InboxUnreachableState & {item: CellsConversationsDragItem}
  >();

  const onConversationsDrop: (item: CellsConversationsDragItem) => void = (item) => {
    if (!props.onConversationsDrop) {
      return;
    }

    if (!props.hasNewMoveToUnreachable || !props.findInboxUnreachableState) {
      props.onConversationsDrop(item);
      return;
    }

    const newInboxUnreachableState = props.findInboxUnreachableState(item.conversationIds, inbox);

    if (!newInboxUnreachableState.isInboxUnreachable) {
      props.onConversationsDrop(item);
      return;
    }

    setInboxUnreachableState({...newInboxUnreachableState, item});
  };

  const maybeRenderInboxUnreachableDialog = () => {
    if (
      !inboxUnreachableState ||
      !inboxUnreachableState.isInboxUnreachable ||
      !inboxUnreachableState.item ||
      !props.onConversationsDrop ||
      !props.hasNewMoveToUnreachable
    ) {
      return null;
    }

    return (
      <MoveAccessDialog
        destinationInbox={inbox}
        conversationsCount={inboxUnreachableState.item.conversationIds.length}
        onKeepAccessClick={() => props.onConversationsDrop?.(inboxUnreachableState.item, true)}
        onLoseAccessClick={() => props.onConversationsDrop?.(inboxUnreachableState.item, false)}
        onRequestClose={() => setInboxUnreachableState(undefined)}
        isUserOnlyFollower={inboxUnreachableState.isUserOnlyFollower}
      />
    );
  };

  const onConversationsDropWithSafetyNets = useConversationIdsHandlerWithSafetyNets<
    [CellsConversationsDragItem]
  >(SafetyNetActionsEnum.MOVE, onConversationsDrop, getConversationIdsFromOptions);

  return (
    <>
      <WorkspaceLineDropTarget
        {...props}
        shouldHidePreviewBadge={props.isEditingWorkspace}
        onConversationsDrop={onConversationsDropWithSafetyNets}
        renderTooltip={() => inbox.name}
        interactionId={makeInteractionId(inbox)}
      >
        <NavigationLineIcon>
          {
            <ColorDot
              size={8}
              color={inboxColor || defaultColor}
              hoverColor={inboxColor || theme.itemDotHover}
            />
          }
        </NavigationLineIcon>
        <NavigationLineLabel isUnread={isUnread} isEditingWorkspace={isEditingWorkspace}>
          <StyledLabelDiv>
            <StyledTextDiv>
              <TooltipOverflowCondition>{inbox.name}</TooltipOverflowCondition>
            </StyledTextDiv>
            {props.shouldShowRefinedResultsIndicator && <StyledWorkspaceItemRefinedResultsIndicator />}
            {props.isRecentlyViewed === false && <WorkspacesEditorRarelyViewedBadge />}
          </StyledLabelDiv>
        </NavigationLineLabel>
        {renderErrorOrBadge(props)}
      </WorkspaceLineDropTarget>
      {maybeRenderInboxUnreachableDialog()}
    </>
  );
};

/*
 * Helpers.
 */

function renderErrorOrBadge(props: WorkspaceInboxItemProps) {
  // If a channel in this inbox has an error, render a warning icon.
  const {channelError, onShowInboxErrorsClick, isEditingWorkspace} = props;
  if (channelError && !isEditingWorkspace) {
    const onErrorClick: EventHandler = (e) => {
      e.preventDefault();
      onShowInboxErrorsClick();
    };
    const actionable = isActionableError(channelError);

    const renderTooltip = () => channelError.description;
    return (
      <NavigationLineErrorIcon
        onClick={onErrorClick}
        isLowlighted={!actionable}
        renderTooltip={renderTooltip}
      />
    );
  }

  return (
    <WorkspaceLineMore
      renderMenu={props.renderMenu}
      badgeValue={props.badgeValue}
      isUnread={props.isUnread}
      loadingType={props.loadingType}
      shouldShowOfflineIcon={props.shouldShowOfflineIcon}
      isEditingWorkspace={props.isEditingWorkspace}
      isChecked={props.isChecked}
      isEditorCheckboxDisabled={props.isEditorCheckboxDisabled}
      shouldRemoveBadgeLeftSpacing={props.isRecentlyViewed === false}
    />
  );
}

function makeInteractionId(inbox: InboxModel) {
  return `workspace-inbox-${inbox.id}`;
}

function getConversationIdsFromOptions(options: CellsConversationsDragItem) {
  return options.conversationIds;
}
